import React, { useState, useEffect } from 'react';
import { App, Input, Select, Switch, DatePicker, Popconfirm, Button, Segmented } from 'antd';
import { HolderOutlined, DownOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import api, { errorCode } from './api';
import SocialIcon from '../components/SocialIcon';
import getEmbed from '../utils/linkEmbed';
import { enums } from '../config';
import { parseWhatsapp, buildWhatsapp } from '../utils/blocks';

export const LINK_TYPES = ['custom', 'instagram', 'tiktok', 'youtube', 'twitter', 'whatsapp', 'spotify', 'email'];

// Same rules the profile page uses to decide whether a link is shown
export const linkStatus = (link, now = Date.now()) => {
  if (link.isActive === false) return 'hidden';
  if (link.scheduledStart && new Date(link.scheduledStart).getTime() > now) return 'scheduled';
  if (link.scheduledEnd && new Date(link.scheduledEnd).getTime() < now) return 'expired';
  return 'live';
};

// editlink overwrites url and title unconditionally, so every save carries
// the whole link, changed fields merged in
const payload = (link, changes) => ({
  _id: link._id,
  linkTitle: link.linkTitle,
  url: link.url,
  type: link.type || 'custom',
  isActive: link.isActive !== false,
  featured: !!link.featured,
  display: link.display || 'button',
  scheduledStart: link.scheduledStart || null,
  scheduledEnd: link.scheduledEnd || null,
  ...changes
});

// Phone number and ready-made message for a WhatsApp link, written back
// into its wa.me URL so people never have to build one by hand
const WhatsappFields = ({ url, onSave }) => {
  const [t] = useTranslation();
  const parsed = parseWhatsapp(url);
  const [phone, setPhone] = useState(parsed.phone);
  const [text, setText] = useState(parsed.message);
  useEffect(() => { const p = parseWhatsapp(url); setPhone(p.phone); setText(p.message); }, [url]);

  const commit = () => {
    // Leaving the fields without editing them saves nothing, even if
    // rebuilding the URL would normalise it differently
    const saved = parseWhatsapp(url);
    if (phone === saved.phone && text === saved.message) return;
    if (!phone.replace(/\D/g, '')) return;
    const next = buildWhatsapp(phone, text.trim());
    if (next !== url) onSave(next);
  };

  return (
    <div className="dash-field dash-field--wide dash-whatsapp">
      <span>{t('blocks.whatsappOrder')}</span>
      <div className="dash-grid">
        <Input value={phone} onChange={e => setPhone(e.target.value)} onBlur={commit} placeholder="0555 123 45 67" addonBefore={t('blocks.phone')} />
        <Input value={text} onChange={e => setText(e.target.value)} onBlur={commit} maxLength={300} placeholder={t('blocks.messagePlaceholder')} />
      </div>
      <span className="dash-hint">{t('blocks.whatsappHint')}</span>
    </div>
  );
};

const LinkRow = ({ link, username, save, dragHandleProps, expanded, onToggle }) => {
  const [t] = useTranslation();
  const { message } = App.useApp();
  const [title, setTitle] = useState(link.linkTitle);
  const [url, setUrl] = useState(link.url);
  const [urlError, setUrlError] = useState('');

  // The server may rewrite what was typed (e.g. add https://); show its version
  useEffect(() => { setTitle(link.linkTitle); }, [link.linkTitle]);
  useEffect(() => { setUrl(link.url); }, [link.url]);

  const update = (changes) =>
    save(api.editLink(username, payload(link, changes))).catch(err => {
      const code = errorCode(err);
      if (code === enums.errorCodes.c13) setUrlError(t('inputErrors.invalidUrl'));
      else if (code === enums.errorCodes.c14) message.warning(t('schedule.invalid'));
      else message.error(t('dash.saveFailed'));
      // Fields show the saved link again; only a rejected URL stays as typed,
      // next to its error, so it can be corrected
      setTitle(link.linkTitle);
      if (code !== enums.errorCodes.c13) setUrl(link.url);
      throw err;
    }).catch(() => {});

  const saveTitle = () => {
    const value = title.trim();
    if (!value) return setTitle(link.linkTitle);
    if (value !== link.linkTitle) update({ linkTitle: value });
  };

  const saveUrl = () => {
    const value = url.trim();
    setUrlError('');
    if (!value) return setUrl(link.url);
    if (value !== link.url) update({ url: value });
  };

  const remove = () => save(api.deleteLink(username, link._id)).catch(() => message.error(t('dash.saveFailed')));

  const status = linkStatus(link);
  const display = link.display || 'button';
  const canIcon = (link.type || 'custom') !== 'custom';
  const canEmbed = !!getEmbed(link.url);
  const onEnter = e => e.target.blur();

  return (
    <div className={`dash-link ${expanded ? 'is-open' : ''} ${status !== 'live' ? 'is-off' : ''}`}>
      <div className="dash-link__row">
        <span className="dash-link__handle" {...dragHandleProps} aria-label={t('dash.dragToReorder')} title={t('dash.dragToReorder')}><HolderOutlined /></span>
        <button type="button" className="dash-link__summary" onClick={onToggle} aria-expanded={expanded}>
          <span className="dash-link__icon"><SocialIcon type={link.type} /></span>
          <span className="dash-link__text">
            <span className="dash-link__title">{link.linkTitle}</span>
            <span className="dash-link__url">{link.url.replace(/^https?:\/\//, '').replace(/^mailto:/, '')}</span>
          </span>
          {display === 'button' && link.featured && <span className="dash-tag dash-tag--featured">{t('dash.featuredTag')}</span>}
          {display !== 'button' && <span className="dash-tag dash-tag--display">{t(display === 'icon' ? 'dash.displayIcon' : 'dash.displayEmbed')}</span>}
          {status !== 'live' && <span className={`dash-tag dash-tag--${status}`}>{t(`schedule.status.${status}`)}</span>}
        </button>
        <Switch size="small" checked={link.isActive !== false} onChange={isActive => update({ isActive })} aria-label={t('schedule.visible')} title={t('schedule.visible')} />
        <button type="button" className="dash-link__chevron" onClick={onToggle} aria-label={t('dash.details')} aria-expanded={expanded} tabIndex={-1}><DownOutlined /></button>
      </div>

      {expanded &&
        <div className="dash-link__body">
          <div className="dash-grid">
            <label className="dash-field">
              <span>{t('title')}</span>
              <Input value={title} onChange={e => setTitle(e.target.value)} onBlur={saveTitle} onPressEnter={onEnter} />
            </label>
            <label className="dash-field">
              <span>{t('url')}</span>
              <Input value={url} onChange={e => { setUrl(e.target.value); setUrlError(''); }} onBlur={saveUrl} onPressEnter={onEnter} status={urlError ? 'error' : ''} />
              {urlError && <span className="dash-error">{urlError}</span>}
            </label>
            {link.type === 'whatsapp' && <WhatsappFields url={link.url} onSave={value => update({ url: value })} />}
            <label className="dash-field">
              <span>{t('dash.type')}</span>
              <Select value={link.type || 'custom'} onChange={type => update({ type })}
                options={LINK_TYPES.map(value => ({ value, label: <span className="dash-type-option"><SocialIcon type={value} />{t(`linkTypes.${value}`)}</span> }))} />
            </label>
            <div className="dash-field">
              <span>{t('display.label')}</span>
              <Segmented block value={display} onChange={value => update({ display: value })}
                options={[
                  { value: 'button', label: t('display.button') },
                  { value: 'icon', label: t('dash.displayIcon'), disabled: !canIcon },
                  { value: 'embed', label: t('dash.displayEmbed'), disabled: !canEmbed }
                ]} />
              {display === 'button' && (!canIcon || !canEmbed) &&
                <span className="dash-hint">{!canIcon ? t('dash.iconHint') : t('dash.embedHint')}</span>}
            </div>
            <div className="dash-field dash-field--wide">
              <span>{t('dash.schedule')}</span>
              <DatePicker.RangePicker
                showTime={{ format: 'HH:mm' }}
                format="DD.MM.YYYY HH:mm"
                allowEmpty={[true, true]}
                placeholder={[t('schedule.start'), t('schedule.end')]}
                value={[
                  link.scheduledStart ? dayjs(link.scheduledStart) : null,
                  link.scheduledEnd ? dayjs(link.scheduledEnd) : null
                ]}
                onChange={dates => {
                  const [start, end] = dates || [null, null];
                  update({ scheduledStart: start ? start.toISOString() : null, scheduledEnd: end ? end.toISOString() : null });
                }}
              />
              <span className="dash-hint">{t('dash.scheduleHint')}</span>
            </div>
          </div>
          <div className="dash-link__foot">
            {display === 'button'
              ? <label className="dash-inline"><Switch size="small" checked={!!link.featured} onChange={featured => update({ featured })} /> {t('dash.featured')}</label>
              : <span />}
            <Popconfirm title={t('dash.deleteConfirm')} okText={t('dash.delete')} cancelText={t('dash.cancel')} okButtonProps={{ danger: true }} onConfirm={remove}>
              <Button danger type="text" icon={<DeleteOutlined />}>{t('dash.delete')}</Button>
            </Popconfirm>
          </div>
        </div>
      }
    </div>
  );
};

export default LinkRow;
